import { useState } from 'react';
import { useAuth } from '../contexts/auth-context';
import { useToast } from '../contexts/toast-context';
import { AUTH_MESSAGES, CONSOLE_MESSAGES } from '../constants';
import { MODAL_CLASSES, SPINNER_CLASSES, cn } from '../styles';

function EmailConfirmationModal({ isOpen, onClose, userEmail }) {
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const { user, resendEmailVerification } = useAuth();
  const { showError, showSuccess } = useToast();

  // Handle modal close
  const handleClose = () => {
    onClose();
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle resend verification email
  const handleResendEmail = async () => {
    setIsResending(true);
    setResendMessage('');

    try {
      const result = await resendEmailVerification();
      
      if (result.success) {
        setResendMessage(AUTH_MESSAGES.VERIFICATION_EMAIL_SENT);
        showSuccess(AUTH_MESSAGES.VERIFICATION_EMAIL_SENT);
      }
    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_RESENDING_EMAIL, error);
      const errorMessage = error.message || AUTH_MESSAGES.VERIFICATION_EMAIL_ERROR;
      setResendMessage(errorMessage);
      showError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={MODAL_CLASSES.OVERLAY}
      onClick={handleOverlayClick}
    >
      <div className="relative w-full max-w-md bg-dark-800 rounded-xl sm:rounded-2xl shadow-2xl border border-dark-700 overflow-hidden max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 p-1.5 text-gray-400 hover:text-white transition-colors"
          aria-label="Uždaryti"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-4 sm:p-5 pb-2 sm:pb-3">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
                Patvirtinkite savo el. paštą
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm">
                Jūsų paskyra sėkmingai sukurta!
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-3">
            {/* Success message */}
            <div className="p-2.5 sm:p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
              <div className="flex items-center justify-center mb-1">
                <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-400 font-medium text-xs sm:text-sm">
                  Registracija sėkminga!
                </span>
              </div>
            </div>

            {/* Email instruction */}
            <div className="text-center space-y-2">
              <p className="text-gray-300 text-xs sm:text-sm">
                Patvirtinimo el. laiškas išsiųstas į:
              </p>
              <div className="bg-dark-700/50 rounded-lg p-2 sm:p-2.5 border border-dark-600">
                <p className="text-white font-medium text-xs sm:text-sm break-all">
                  {userEmail || user?.email}
                </p>
              </div>
            </div>

            {/* Compact instructions */}
            <div className="bg-dark-700/30 rounded-lg p-2.5 sm:p-3 space-y-1.5 sm:space-y-2">
              <div className="flex items-center text-white font-medium text-xs sm:text-sm">
                <svg className="w-4 h-4 text-primary-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Kitas žingsnis:
              </div>
              <p className="text-gray-400 text-[11px] sm:text-sm leading-relaxed pl-6">
                Patikrinkite el. paštą → Atidarykite FilmųDB laišką → Spauskite patvirtinimo nuorodą → Prisijunkite!
              </p>
            </div>

            {/* Important notice - more compact */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-2 sm:p-2.5">
              <div className="flex items-start space-x-2">
                <svg className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="text-yellow-300 text-[11px] sm:text-xs font-medium">
                    Patikrinkite šlamšto aplanką. Nuoroda galioja 24 val.
                  </p>
                </div>
              </div>
            </div>

            {/* Resend email section - more compact */}
            <div className="text-center">
              {resendMessage && (
                <div className={`p-2 rounded-lg mb-2 text-[11px] sm:text-xs ${
                  resendMessage.includes('sėkmingai') 
                    ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`}>
                  {resendMessage}
                </div>
              )}

              <div className="space-y-2">
                <p className="text-gray-400 text-xs">
                  Negavote el. laiško?
                </p>
                <button
                  onClick={handleResendEmail}
                  disabled={isResending}
                  className="text-primary-400 hover:text-primary-300 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  {isResending ? (
                    <span className="flex items-center justify-center">
                      <div className={cn(SPINNER_CLASSES.SMALL_PRIMARY, 'mr-2')}></div>
                      Siunčiama...
                    </span>
                  ) : (
                    'Siųsti dar kartą'
                  )}
                </button>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={() => window.location.reload()}
              className="btn-primary w-full justify-center h-10 sm:h-11 text-sm sm:text-base"
            >
              Supratau
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailConfirmationModal; 